import { useEffect, useRef, useState } from "react";
import EmailBodyEditor from "components/EmailBodyEditor";
import ReactQuill from "react-quill";
import {
  MULTIPLE_SHIPMENT_TEMPLATE_VARIABLES,
  SHIPMENT_TEMPLATE_VARIABLES,
} from "pages/Sales/UpsTracking/shipmentEmailTemplate";

export const LOCATION_EMAIL_TEMPLATE_MODULES = [
  {
    key: "quotes",
    templateType: "quotes",
    typeAliases: ["quotes", "quote"],
    label: "Quotes",
    subjectKey: "quoteEmailSubject",
    formatKey: "quoteEmailFormat",
    // sendPdfKey: "quoteSendPdf",
    // ccSalesPersonKey: "quoteCcSalesPerson",
    variables: {
      //   ID: " << ID  >> ",
      "Q-ID": " << Quote ID >> ",
      "CUSTOMER ID": " << Customer ID >> ",
      "CUSTOMER NAME": " << Customer Name >> ",
      "JOB NAME": " << Job Name >> ",
      "FIRST NAME": " << First Name >> ",
      "LAST NAME": " << Last Name >> ",
      "LOCATION NAME": " << Location Name >> ",

      //   "CONT ING ADDRESS": " << Shipping Address >> ",
    },
  },
  {
    key: "approval",
    templateType: "approval",
    typeAliases: ["approval", "approvals"],
    label: "Order Approval",
    subjectKey: "approvalEmailSubject",
    formatKey: "approvalEmailFormat",
    sendPdfKey: "approvalSendPdf",
    ccSalesPersonKey: "approvalCcSalesPerson",
    sendEmailConfirmationKey: "approvalSendConfirmation",
    confirmationFormatKey: "approvalConfirmationTemplate",
    confirmationSubjectKey: "approvalConfirmationSubject",
    sendQuestionKey: "sendQuestion",
    questionSubjectKey: "questionSubject",
    questionFormatKey: "questionTemplate",
    sendFeedbackKey: "sendFeedback",
    feedbackSubjectKey: "feedbackSubject",
    feedbackFormatKey: "feedbackTemplate",
    variables: {
      "W-ID": " << ID >> ",
      "CUSTOMER ID": " << Customer ID >> ",
      "CUSTOMER NAME": " << Customer Name >> ",
      "JOB NAME": " << Job Name >> ",
      "FIRST NAME": " << First Name >> ",
      "LAST NAME": " << Last Name >> ",
      "LOCATION NAME": " << Location Name >> ",
      "APPROVAL LINK": " << Approval Link >> ",
      "CUSTOMER LINK": " << Customer Link >> ",
    },
  },
  {
    // Shipments -> Email (user, 2026-10-26: "Email template location wise
    // with variables related to shipment"). Filled by
    // Sales/UpsTracking/shipmentEmailTemplate.js fillShipmentTemplate; a
    // location with no Shipment template sends the built-in email.
    //
    // Two sub-tabs under one "Shipment" tab (user, 2026-10-27): `group` puts
    // modules under one top tab, `subLabel` names each sub-tab.
    key: "shipment",
    templateType: "shipment",
    typeAliases: ["shipment", "shipments"],
    label: "Shipment",
    group: "shipment",
    subLabel: "Single Shipment",
    subjectKey: "shipmentEmailSubject",
    formatKey: "shipmentEmailFormat",
    variables: SHIPMENT_TEMPLATE_VARIABLES,
    lexical: true,
  },
  {
    // Shipments -> Bulk Email (user, 2026-10-27): several shipments in one
    // email; << Shipments >> is the list. Filled by
    // fillMultipleShipmentTemplate.
    key: "shipmentMultiple",
    templateType: "shipment-multiple",
    typeAliases: ["shipment-multiple"],
    label: "Shipment",
    group: "shipment",
    subLabel: "Multiple Shipments",
    subjectKey: "shipmentMultipleEmailSubject",
    formatKey: "shipmentMultipleEmailFormat",
    variables: MULTIPLE_SHIPMENT_TEMPLATE_VARIABLES,
    lexical: true,
  },
  {
    // W Orders -> Ready to Pickup (user, 2026-10-27), in the QuickView and
    // Details menus. Filled by LotApproval/SendWoFeedback (variant
    // "ready-pickup").
    key: "readyPickup",
    templateType: "ready-pickup",
    typeAliases: ["ready-pickup"],
    label: "Ready for Pickup",
    subjectKey: "readyPickupEmailSubject",
    formatKey: "readyPickupEmailFormat",
    // Body edited in the same Lexical editor the send modals use (user,
    // 2026-10-30), so what is saved is what is sent.
    lexical: true,
    variables: {
      "CUSTOMER ID": " << Customer ID >> ",
      "CUSTOMER NAME": " << Customer Name >> ",
      "W-ID": " << ID >> ",
      "JOB NAME": " << Job Name >> ",
      "FIRST NAME": " << First Name >> ",
      "LAST NAME": " << Last Name >> ",
      "LOCATION NAME": " << Location Name >> ",
    },
  },
];

// The top tabs: one per group (a module with no group is its own), in the
// order the modules are listed.
const TEMPLATE_GROUPS = LOCATION_EMAIL_TEMPLATE_MODULES.reduce((groups, m) => {
  const id = m.group || m.key;
  const found = groups.find((g) => g.id === id);
  if (found) found.modules.push(m);
  else groups.push({ id, label: m.label, modules: [m] });
  return groups;
}, []);

// "Get Feedback" is shown as its own tab, but its data is still stored on the
// Order Approval template row (feedbackSubject / feedbackTemplate / sendFeedback).
export const FEEDBACK_TAB_KEY = "feedback";

export const LOCATION_EMAIL_EDITOR_MODULES = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
    ["clean"],
  ],
  clipboard: { matchVisual: false },
};

const getTemplateValue = (data, moduleConfig, field) => {
  const nestedTemplate =
    data?.moduleEmailTemplates?.[moduleConfig.key] ||
    data?.defaultEmailFormats?.[moduleConfig.key] ||
    data?.locationEmailTemplates?.[moduleConfig.key] ||
    {};

  const flatFieldKey =
    field === "subject"
      ? moduleConfig.subjectKey
      : field === "confirmationFormat"
        ? moduleConfig.confirmationFormatKey
        : field === "confirmationSubject"
          ? moduleConfig.confirmationSubjectKey
          : field === "questionFormat"
            ? moduleConfig.questionFormatKey
            : field === "questionSubject"
              ? moduleConfig.questionSubjectKey
              : field === "feedbackFormat"
                ? moduleConfig.feedbackFormatKey
                : field === "feedbackSubject"
                  ? moduleConfig.feedbackSubjectKey
                  : moduleConfig.formatKey;

  return nestedTemplate?.[field] || data?.[flatFieldKey] || "";
};

const getTemplateBooleanValue = (data, moduleConfig, field) => {
  const nestedTemplate =
    data?.moduleEmailTemplates?.[moduleConfig.key] ||
    data?.defaultEmailFormats?.[moduleConfig.key] ||
    data?.locationEmailTemplates?.[moduleConfig.key] ||
    {};

  const flatFieldKey =
    field === "sendPdf"
      ? moduleConfig.sendPdfKey
      : field === "sendConfirmation"
        ? moduleConfig.sendEmailConfirmationKey
        : field === "sendQuestion"
          ? moduleConfig.sendQuestionKey
          : field === "sendFeedback"
            ? moduleConfig.sendFeedbackKey
            : moduleConfig.ccSalesPersonKey;

  if (typeof nestedTemplate?.[field] === "boolean") {
    return nestedTemplate[field];
  }

  if (typeof data?.[flatFieldKey] === "boolean") {
    return data[flatFieldKey];
  }

  return false;
};

export const buildLocationEmailTemplates = (data = {}) =>
  LOCATION_EMAIL_TEMPLATE_MODULES.reduce((accumulator, moduleConfig) => {
    accumulator[moduleConfig.key] = {
      subject: getTemplateValue(data, moduleConfig, "subject"),
      format: getTemplateValue(data, moduleConfig, "format"),
      ...(moduleConfig.confirmationFormatKey
        ? {
            confirmationFormat: getTemplateValue(
              data,
              moduleConfig,
              "confirmationFormat",
            ),
          }
        : {}),
      ...(moduleConfig.confirmationSubjectKey
        ? {
            confirmationSubject: getTemplateValue(
              data,
              moduleConfig,
              "confirmationSubject",
            ),
          }
        : {}),
      ...(moduleConfig.questionFormatKey
        ? {
            questionFormat: getTemplateValue(
              data,
              moduleConfig,
              "questionFormat",
            ),
          }
        : {}),
      ...(moduleConfig.questionSubjectKey
        ? {
            questionSubject: getTemplateValue(
              data,
              moduleConfig,
              "questionSubject",
            ),
          }
        : {}),
      ...(moduleConfig.feedbackFormatKey
        ? {
            feedbackFormat: getTemplateValue(
              data,
              moduleConfig,
              "feedbackFormat",
            ),
          }
        : {}),
      ...(moduleConfig.feedbackSubjectKey
        ? {
            feedbackSubject: getTemplateValue(
              data,
              moduleConfig,
              "feedbackSubject",
            ),
          }
        : {}),
      sendPdf: getTemplateBooleanValue(data, moduleConfig, "sendPdf"),
      ccSalesPerson: getTemplateBooleanValue(
        data,
        moduleConfig,
        "ccSalesPerson",
      ),
      sendConfirmation: getTemplateBooleanValue(
        data,
        moduleConfig,
        "sendConfirmation",
      ),
      sendQuestion: getTemplateBooleanValue(data, moduleConfig, "sendQuestion"),
      sendFeedback: getTemplateBooleanValue(data, moduleConfig, "sendFeedback"),
    };
    return accumulator;
  }, {});

export const withLocationEmailTemplateFields = (data = {}) => {
  const templates = buildLocationEmailTemplates(data);

  const flattenedTemplateFields = LOCATION_EMAIL_TEMPLATE_MODULES.reduce(
    (accumulator, moduleConfig) => {
      const next = {
        ...accumulator,
        [moduleConfig.subjectKey]: templates[moduleConfig.key].subject,
        [moduleConfig.formatKey]: templates[moduleConfig.key].format,
      };

      if (moduleConfig.sendPdfKey) {
        next[moduleConfig.sendPdfKey] = templates[moduleConfig.key].sendPdf;
      }

      if (moduleConfig.ccSalesPersonKey) {
        next[moduleConfig.ccSalesPersonKey] =
          templates[moduleConfig.key].ccSalesPerson;
      }

      if (moduleConfig.sendEmailConfirmationKey) {
        next[moduleConfig.sendEmailConfirmationKey] =
          templates[moduleConfig.key].sendConfirmation;
      }

      if (moduleConfig.confirmationFormatKey) {
        next[moduleConfig.confirmationFormatKey] =
          templates[moduleConfig.key].confirmationFormat || "";
      }

      if (moduleConfig.confirmationSubjectKey) {
        next[moduleConfig.confirmationSubjectKey] =
          templates[moduleConfig.key].confirmationSubject || "";
      }

      if (moduleConfig.sendQuestionKey) {
        next[moduleConfig.sendQuestionKey] =
          templates[moduleConfig.key].sendQuestion;
      }

      if (moduleConfig.questionFormatKey) {
        next[moduleConfig.questionFormatKey] =
          templates[moduleConfig.key].questionFormat || "";
      }

      if (moduleConfig.questionSubjectKey) {
        next[moduleConfig.questionSubjectKey] =
          templates[moduleConfig.key].questionSubject || "";
      }

      if (moduleConfig.sendFeedbackKey) {
        next[moduleConfig.sendFeedbackKey] =
          templates[moduleConfig.key].sendFeedback;
      }

      if (moduleConfig.feedbackFormatKey) {
        next[moduleConfig.feedbackFormatKey] =
          templates[moduleConfig.key].feedbackFormat || "";
      }

      if (moduleConfig.feedbackSubjectKey) {
        next[moduleConfig.feedbackSubjectKey] =
          templates[moduleConfig.key].feedbackSubject || "";
      }

      return next;
    },
    {},
  );

  return {
    ...data,
    ...flattenedTemplateFields,
    moduleEmailTemplates: templates,
    defaultEmailFormats: templates,
    locationEmailTemplates: templates,
  };
};

export const stripLocationTemplateFields = (data = {}) => {
  const updated = { ...data };

  delete updated.moduleEmailTemplates;
  delete updated.defaultEmailFormats;
  delete updated.locationEmailTemplates;

  LOCATION_EMAIL_TEMPLATE_MODULES.forEach((moduleConfig) => {
    delete updated[moduleConfig.subjectKey];
    delete updated[moduleConfig.formatKey];
    if (moduleConfig.sendPdfKey) {
      delete updated[moduleConfig.sendPdfKey];
    }
    if (moduleConfig.ccSalesPersonKey) {
      delete updated[moduleConfig.ccSalesPersonKey];
    }
    if (moduleConfig.sendEmailConfirmationKey) {
      delete updated[moduleConfig.sendEmailConfirmationKey];
    }
    if (moduleConfig.confirmationFormatKey) {
      delete updated[moduleConfig.confirmationFormatKey];
    }
    if (moduleConfig.confirmationSubjectKey) {
      delete updated[moduleConfig.confirmationSubjectKey];
    }
    if (moduleConfig.sendQuestionKey) {
      delete updated[moduleConfig.sendQuestionKey];
    }
    if (moduleConfig.questionFormatKey) {
      delete updated[moduleConfig.questionFormatKey];
    }
    if (moduleConfig.questionSubjectKey) {
      delete updated[moduleConfig.questionSubjectKey];
    }
    if (moduleConfig.sendFeedbackKey) {
      delete updated[moduleConfig.sendFeedbackKey];
    }
    if (moduleConfig.feedbackFormatKey) {
      delete updated[moduleConfig.feedbackFormatKey];
    }
    if (moduleConfig.feedbackSubjectKey) {
      delete updated[moduleConfig.feedbackSubjectKey];
    }
  });

  return updated;
};

const normalizeType = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getModuleByTemplateType = (type) => {
  const normalizedType = normalizeType(type);
  return LOCATION_EMAIL_TEMPLATE_MODULES.find((moduleConfig) =>
    moduleConfig.typeAliases.some(
      (alias) => normalizeType(alias) === normalizedType,
    ),
  );
};

export const applyApiTemplatesToLocationData = (
  locationData = {},
  templates = [],
) => {
  const mergedData = withLocationEmailTemplateFields(locationData);
  const moduleEmailTemplates = buildLocationEmailTemplates(mergedData);

  (templates || []).forEach((templateRow) => {
    const matchedModule = getModuleByTemplateType(templateRow?.type);
    if (!matchedModule) {
      return;
    }

    moduleEmailTemplates[matchedModule.key] = {
      subject: templateRow?.subject || "",
      format: templateRow?.template || "",
      ...(matchedModule.confirmationFormatKey
        ? {
            confirmationFormat: templateRow?.approvalConfirmationTemplate || "",
          }
        : {}),
      ...(matchedModule.confirmationSubjectKey
        ? {
            confirmationSubject: templateRow?.approvalConfirmationSubject || "",
          }
        : {}),
      ...(matchedModule.questionFormatKey
        ? {
            questionFormat: templateRow?.questionTemplate || "",
          }
        : {}),
      ...(matchedModule.questionSubjectKey
        ? {
            questionSubject: templateRow?.questionSubject || "",
          }
        : {}),
      ...(matchedModule.feedbackFormatKey
        ? {
            feedbackFormat: templateRow?.feedbackTemplate || "",
          }
        : {}),
      ...(matchedModule.feedbackSubjectKey
        ? {
            feedbackSubject: templateRow?.feedbackSubject || "",
          }
        : {}),
      sendPdf: Boolean(templateRow?.sendPdf),
      ccSalesPerson: Boolean(templateRow?.ccSalesPerson),
      sendConfirmation: Boolean(templateRow?.sendConfirmation),
      sendQuestion: Boolean(templateRow?.sendQuestion),
      sendFeedback: Boolean(templateRow?.sendFeedback),
    };
  });

  return withLocationEmailTemplateFields({
    ...mergedData,
    moduleEmailTemplates,
  });
};

export const validateLocationEmailTemplates = (formData = {}) => {
  const templates = buildLocationEmailTemplates(formData);
  const errors = {};

  LOCATION_EMAIL_TEMPLATE_MODULES.forEach((moduleConfig) => {
    const tpl = templates[moduleConfig.key];
    const moduleErrors = {};

    // Validate confirmation email fields
    if (tpl?.sendConfirmation) {
      if (
        moduleConfig.confirmationSubjectKey &&
        !tpl.confirmationSubject?.trim()
      ) {
        moduleErrors.confirmationSubject =
          "Approval Confirmation email subject is required when 'Send Confirmation' is enabled.";
      }
      if (
        moduleConfig.confirmationFormatKey &&
        !tpl.confirmationFormat?.replace(/<[^>]*>/g, "").trim()
      ) {
        moduleErrors.confirmationFormat =
          "Approval Confirmation email template is required when 'Send Confirmation' is enabled.";
      }
    }

    // Validate question fields
    if (tpl?.sendQuestion) {
      if (moduleConfig.questionSubjectKey && !tpl.questionSubject?.trim()) {
        moduleErrors.questionSubject =
          "Question subject is required when 'Ask a question' is enabled.";
      }
      if (
        moduleConfig.questionFormatKey &&
        !tpl.questionFormat?.replace(/<[^>]*>/g, "").trim()
      ) {
        moduleErrors.questionFormat =
          "Question template is required when 'Ask a question' is enabled.";
      }
    }

    // Validate feedback fields
    if (tpl?.sendFeedback) {
      if (moduleConfig.feedbackSubjectKey && !tpl.feedbackSubject?.trim()) {
        moduleErrors.feedbackSubject =
          "Feedback subject is required when 'Get Feedback' is enabled.";
      }
      if (
        moduleConfig.feedbackFormatKey &&
        !tpl.feedbackFormat?.replace(/<[^>]*>/g, "").trim()
      ) {
        moduleErrors.feedbackFormat =
          "Feedback template is required when 'Get Feedback' is enabled.";
      }
    }

    if (Object.keys(moduleErrors).length) {
      errors[moduleConfig.key] = moduleErrors;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const getTemplateApiPayloadList = (formData = {}) => {
  const templates = buildLocationEmailTemplates(formData);

  return LOCATION_EMAIL_TEMPLATE_MODULES.map((moduleConfig) => ({
    type: moduleConfig.templateType,
    subject: templates[moduleConfig.key]?.subject || "",
    template: templates[moduleConfig.key]?.format || "",
    ...(moduleConfig.confirmationFormatKey
      ? {
          approvalConfirmationTemplate:
            templates[moduleConfig.key]?.confirmationFormat || "",
        }
      : {}),
    ...(moduleConfig.confirmationSubjectKey
      ? {
          approvalConfirmationSubject:
            templates[moduleConfig.key]?.confirmationSubject || "",
        }
      : {}),
    ...(moduleConfig.questionFormatKey
      ? {
          questionTemplate: templates[moduleConfig.key]?.questionFormat || "",
        }
      : {}),
    ...(moduleConfig.questionSubjectKey
      ? {
          questionSubject: templates[moduleConfig.key]?.questionSubject || "",
        }
      : {}),
    ...(moduleConfig.feedbackFormatKey
      ? {
          feedbackTemplate: templates[moduleConfig.key]?.feedbackFormat || "",
        }
      : {}),
    ...(moduleConfig.feedbackSubjectKey
      ? {
          feedbackSubject: templates[moduleConfig.key]?.feedbackSubject || "",
        }
      : {}),
    sendPdf: Boolean(templates[moduleConfig.key]?.sendPdf),
    ccSalesPerson: Boolean(templates[moduleConfig.key]?.ccSalesPerson),
    sendConfirmation: Boolean(templates[moduleConfig.key]?.sendConfirmation),
    sendQuestion: Boolean(templates[moduleConfig.key]?.sendQuestion),
    sendFeedback: Boolean(templates[moduleConfig.key]?.sendFeedback),
  }));
};

function LocationEmailTemplateFields({ formData, setFormData }) {
  const quillRef = useRef(null);
  const lexicalRef = useRef(null);
  // The Lexical body is loaded again only when the template changes from
  // outside it -- e.g. the location's templates arriving after the form --
  // never from its own typing.
  const lexicalOwnHtml = useRef(null);
  const [lexicalLoad, setLexicalLoad] = useState(0);
  const confirmationQuillRef = useRef(null);
  const questionQuillRef = useRef(null);
  const feedbackTextareaRef = useRef(null);
  const [activeModule, setActiveModule] = useState(
    LOCATION_EMAIL_TEMPLATE_MODULES[0].key,
  );

  const emailTemplates = buildLocationEmailTemplates(formData);

  // The module whose template row owns the feedback fields (Order Approval).
  const feedbackModuleConfig = LOCATION_EMAIL_TEMPLATE_MODULES.find(
    (moduleConfig) => moduleConfig.sendFeedbackKey,
  );
  const isFeedbackTab = activeModule === FEEDBACK_TAB_KEY;

  // On the Feedback tab these resolve to the approval module so its feedback
  // fields (and variables) are read/written against the correct storage key.
  const activeModuleConfig = isFeedbackTab
    ? feedbackModuleConfig
    : LOCATION_EMAIL_TEMPLATE_MODULES.find(
        (moduleConfig) => moduleConfig.key === activeModule,
      );
  const activeStorageKey = activeModuleConfig?.key;

  const showSendPdf = Boolean(activeModuleConfig?.sendPdfKey);
  const showCcSalesPerson = Boolean(activeModuleConfig?.ccSalesPersonKey);
  const showSendConfirmation = Boolean(
    activeModuleConfig?.sendEmailConfirmationKey,
  );
  const showsAskQuestion = Boolean(activeModuleConfig?.sendQuestionKey);
  const showsGetFeedback = Boolean(activeModuleConfig?.sendFeedbackKey);

  const activeTemplate = emailTemplates[activeStorageKey];
  const isSendConfirmationEnabled = activeTemplate?.sendConfirmation;
  const issendQuestionEnabled = activeTemplate?.sendQuestion;
  const isGetFeedbackEnabled = activeTemplate?.sendFeedback;

  const confirmationErrors =
    showSendConfirmation && isSendConfirmationEnabled
      ? {
          subject:
            activeModuleConfig?.confirmationSubjectKey &&
            !activeTemplate?.confirmationSubject?.trim(),
          format:
            activeModuleConfig?.confirmationFormatKey &&
            !activeTemplate?.confirmationFormat?.replace(/<[^>]*>/g, "").trim(),
        }
      : { subject: false, format: false };

  const questionErrors =
    showsAskQuestion && issendQuestionEnabled
      ? {
          subject:
            activeModuleConfig?.questionSubjectKey &&
            !activeTemplate?.questionSubject?.trim(),
          format:
            activeModuleConfig?.questionFormatKey &&
            !activeTemplate?.questionFormat?.replace(/<[^>]*>/g, "").trim(),
        }
      : { subject: false, format: false };

  const feedbackErrors =
    showsGetFeedback && isGetFeedbackEnabled
      ? {
          subject:
            activeModuleConfig?.feedbackSubjectKey &&
            !activeTemplate?.feedbackSubject?.trim(),
          format:
            activeModuleConfig?.feedbackFormatKey &&
            !activeTemplate?.feedbackFormat?.replace(/<[^>]*>/g, "").trim(),
        }
      : { subject: false, format: false };

  const updateTemplateField = (field, value) => {
    // Update only the raw field in formData, not the computed template fields
    setFormData((prev) => {
      // Remove computed template fields to avoid pollution
      const cleaned = { ...prev };
      delete cleaned.moduleEmailTemplates;
      delete cleaned.defaultEmailFormats;
      delete cleaned.locationEmailTemplates;

      // Find the keys for the current module. On the Feedback tab this resolves
      // to the approval module, where the feedback fields are stored.
      const moduleConfig = LOCATION_EMAIL_TEMPLATE_MODULES.find(
        (m) => m.key === activeStorageKey,
      );
      if (!moduleConfig) return prev;

      // Map field to the correct key in formData
      let fieldKey = null;
      if (field === "subject") fieldKey = moduleConfig.subjectKey;
      else if (field === "format") fieldKey = moduleConfig.formatKey;
      else if (field === "sendPdf") fieldKey = moduleConfig.sendPdfKey;
      else if (field === "ccSalesPerson")
        fieldKey = moduleConfig.ccSalesPersonKey;
      else if (field === "sendConfirmation")
        fieldKey = moduleConfig.sendEmailConfirmationKey;
      else if (field === "confirmationFormat")
        fieldKey = moduleConfig.confirmationFormatKey;
      else if (field === "confirmationSubject")
        fieldKey = moduleConfig.confirmationSubjectKey;
      else if (field === "questionFormat")
        fieldKey = moduleConfig.questionFormatKey;
      else if (field === "questionSubject")
        fieldKey = moduleConfig.questionSubjectKey;
      else if (field === "sendQuestion")
        fieldKey = moduleConfig.sendQuestionKey;
      else if (field === "feedbackFormat")
        fieldKey = moduleConfig.feedbackFormatKey;
      else if (field === "feedbackSubject")
        fieldKey = moduleConfig.feedbackSubjectKey;
      else if (field === "sendFeedback")
        fieldKey = moduleConfig.sendFeedbackKey;

      if (fieldKey) {
        const updated = { ...cleaned, [fieldKey]: value };

        return updated;
      }
      // fallback: update as-is
      return { ...cleaned, [field]: value };
    });
  };

  const lexicalFormat = activeModuleConfig?.lexical
    ? emailTemplates[activeModule]?.format || ""
    : null;
  useEffect(() => {
    if (lexicalFormat !== null && lexicalFormat !== lexicalOwnHtml.current) {
      lexicalOwnHtml.current = lexicalFormat;
      setLexicalLoad((n) => n + 1);
    }
  }, [lexicalFormat]);

  const insertVariable = (variable, ref = quillRef) => {
    if (ref === quillRef && activeModuleConfig?.lexical) {
      lexicalRef.current?.insertText(` ${variable} `);
      return;
    }
    const editor = ref.current?.getEditor();
    if (!editor) {
      return;
    }

    const range = editor.getSelection(true) || { index: 0 };
    const insertIndex = range.index || 0;
    editor.insertText(insertIndex, ` ${variable} `);
    editor.setSelection(insertIndex + variable.length + 2);
  };

  // The feedback template is raw editable HTML in a <textarea>, not a
  // ReactQuill instance -- variables get inserted at the cursor position
  // directly in the text.
  const insertVariableIntoFeedbackTextarea = (variable) => {
    const textarea = feedbackTextareaRef.current;
    const currentValue = emailTemplates[activeStorageKey]?.feedbackFormat || "";
    if (!textarea) {
      updateTemplateField("feedbackFormat", `${currentValue} ${variable} `);
      return;
    }

    const start = textarea.selectionStart ?? currentValue.length;
    const end = textarea.selectionEnd ?? currentValue.length;
    const nextValue =
      currentValue.slice(0, start) + variable + currentValue.slice(end);
    updateTemplateField("feedbackFormat", nextValue);

    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + variable.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const root = editor.root;
    const cleanQuillText = (raw) =>
      raw
        .replace(/\u00A0/g, " ")
        .replace(/\r\n/g, "\n")
        .replace(/^\n+|\n+$/g, "");

    const buildCleanHtml = (cleaned) => {
      // Try to grab the rich HTML from the native selection and convert
      // <p> blocks to simple <br>-separated content so paste targets
      // don't double-space lines.
      try {
        const nativeSel = window.getSelection();
        if (nativeSel && nativeSel.rangeCount > 0) {
          const fragment = nativeSel.getRangeAt(0).cloneContents();
          const wrapper = document.createElement("div");
          wrapper.appendChild(fragment);
          const paras = Array.from(wrapper.querySelectorAll("p"));
          if (paras.length) {
            paras.forEach((p, i) => {
              while (p.firstChild) {
                p.parentNode.insertBefore(p.firstChild, p);
              }
              if (i < paras.length - 1) {
                p.parentNode.insertBefore(document.createElement("br"), p);
              }
              p.parentNode.removeChild(p);
            });
            return wrapper.innerHTML;
          }
        }
      } catch (_) {
        // fall through
      }
      // Fallback: escape the plain text and use <br> for newlines
      return cleaned
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
    };

    const onCopyOrCut = (e) => {
      try {
        const selection = editor.getSelection();
        const text =
          selection && selection.length > 0
            ? editor.getText(selection.index, selection.length)
            : editor.getText();
        const cleaned = cleanQuillText(text);
        const richHtml = buildCleanHtml(cleaned);

        e.clipboardData.setData("text/plain", cleaned);
        e.clipboardData.setData("text/html", richHtml);

        // For cut, also remove the selected content
        if (e.type === "cut" && selection && selection.length > 0) {
          editor.deleteText(selection.index, selection.length);
        }

        e.stopImmediatePropagation();
        e.preventDefault();
      } catch (err) {
        // fall back to default behavior
      }
    };

    const onPaste = (e) => {
      try {
        // Prefer text/plain to avoid <p>-wrapped content from external
        // rich-text sources. Fall back to text/html → stripped tags.
        let text = e.clipboardData.getData("text/plain");
        if (!text) {
          const html = e.clipboardData.getData("text/html");
          if (html) {
            const tmp = document.createElement("div");
            tmp.innerHTML = html
              .replace(/<br\s*\/?>/gi, "\n")
              .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
              .replace(/<\/?[^>]+>/g, "");
            text = tmp.textContent || "";
          }
        }
        if (!text) return;

        e.stopImmediatePropagation();
        e.preventDefault();

        const cleaned = text
          .replace(/\r\n/g, "\n")
          .replace(/\r/g, "\n")
          .replace(/^\n+|\n+$/g, "");

        const sel = editor.getSelection(true) || { index: 0, length: 0 };

        if (sel.length > 0) {
          editor.deleteText(sel.index, sel.length);
        }

        const lines = cleaned.split("\n");
        let insertIndex = sel.index;
        lines.forEach((line, i) => {
          if (i > 0) {
            editor.insertText(insertIndex, "\n", "user");
            insertIndex += 1;
          }
          if (line) {
            editor.insertText(insertIndex, line, "user");
            insertIndex += line.length;
          }
        });

        editor.setSelection(insertIndex, 0);
      } catch (err) {
        // fall back to default behavior
      }
    };

    // Use capture phase to fire BEFORE Quill's own clipboard handlers
    root.addEventListener("copy", onCopyOrCut, true);
    root.addEventListener("cut", onCopyOrCut, true);
    root.addEventListener("paste", onPaste, true);
    return () => {
      root.removeEventListener("copy", onCopyOrCut, true);
      root.removeEventListener("cut", onCopyOrCut, true);
      root.removeEventListener("paste", onPaste, true);
    };
  }, [activeModule]);

  // Clipboard handlers for the confirmation template editor
  useEffect(() => {
    const editor = confirmationQuillRef.current?.getEditor();
    if (!editor) return;

    const root = editor.root;
    const cleanQuillText = (raw) =>
      raw
        .replace(/\u00A0/g, " ")
        .replace(/\r\n/g, "\n")
        .replace(/^\n+|\n+$/g, "");

    const onCopyOrCut = (e) => {
      try {
        const selection = editor.getSelection();
        const text =
          selection && selection.length > 0
            ? editor.getText(selection.index, selection.length)
            : editor.getText();
        const cleaned = cleanQuillText(text);
        e.clipboardData.setData("text/plain", cleaned);
        if (e.type === "cut" && selection && selection.length > 0) {
          editor.deleteText(selection.index, selection.length);
        }
        e.stopImmediatePropagation();
        e.preventDefault();
      } catch (err) {
        // fall back to default behavior
      }
    };

    const onPaste = (e) => {
      try {
        let text = e.clipboardData.getData("text/plain");
        if (!text) {
          const html = e.clipboardData.getData("text/html");
          if (html) {
            const tmp = document.createElement("div");
            tmp.innerHTML = html
              .replace(/<br\s*\/?>/gi, "\n")
              .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
              .replace(/<\/?[^>]+>/g, "");
            text = tmp.textContent || "";
          }
        }
        if (!text) return;
        e.stopImmediatePropagation();
        e.preventDefault();
        const cleaned = text
          .replace(/\r\n/g, "\n")
          .replace(/\r/g, "\n")
          .replace(/^\n+|\n+$/g, "");
        const sel = editor.getSelection(true) || { index: 0, length: 0 };
        if (sel.length > 0) editor.deleteText(sel.index, sel.length);
        const lines = cleaned.split("\n");
        let insertIndex = sel.index;
        lines.forEach((line, i) => {
          if (i > 0) {
            editor.insertText(insertIndex, "\n", "user");
            insertIndex += 1;
          }
          if (line) {
            editor.insertText(insertIndex, line, "user");
            insertIndex += line.length;
          }
        });
        editor.setSelection(insertIndex, 0);
      } catch (err) {
        // fall back to default behavior
      }
    };

    root.addEventListener("copy", onCopyOrCut, true);
    root.addEventListener("cut", onCopyOrCut, true);
    root.addEventListener("paste", onPaste, true);
    return () => {
      root.removeEventListener("copy", onCopyOrCut, true);
      root.removeEventListener("cut", onCopyOrCut, true);
      root.removeEventListener("paste", onPaste, true);
    };
  }, [activeModule, isSendConfirmationEnabled]);

  // Clipboard handlers for the question template editor
  useEffect(() => {
    const editor = questionQuillRef.current?.getEditor();
    if (!editor) return;

    const root = editor.root;
    const cleanQuillText = (raw) =>
      raw
        .replace(/\u00A0/g, " ")
        .replace(/\r\n/g, "\n")
        .replace(/^\n+|\n+$/g, "");

    const onCopyOrCut = (e) => {
      try {
        const selection = editor.getSelection();
        const text =
          selection && selection.length > 0
            ? editor.getText(selection.index, selection.length)
            : editor.getText();
        const cleaned = cleanQuillText(text);
        e.clipboardData.setData("text/plain", cleaned);
        if (e.type === "cut" && selection && selection.length > 0) {
          editor.deleteText(selection.index, selection.length);
        }
        e.stopImmediatePropagation();
        e.preventDefault();
      } catch (err) {
        // fall back to default behavior
      }
    };

    const onPaste = (e) => {
      try {
        let text = e.clipboardData.getData("text/plain");
        if (!text) {
          const html = e.clipboardData.getData("text/html");
          if (html) {
            const tmp = document.createElement("div");
            tmp.innerHTML = html
              .replace(/<br\s*\/?>/gi, "\n")
              .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
              .replace(/<\/?[^>]+>/g, "");
            text = tmp.textContent || "";
          }
        }
        if (!text) return;
        e.stopImmediatePropagation();
        e.preventDefault();
        const cleaned = text
          .replace(/\r\n/g, "\n")
          .replace(/\r/g, "\n")
          .replace(/^\n+|\n+$/g, "");
        const sel = editor.getSelection(true) || { index: 0, length: 0 };
        if (sel.length > 0) editor.deleteText(sel.index, sel.length);
        const lines = cleaned.split("\n");
        let insertIndex = sel.index;
        lines.forEach((line, i) => {
          if (i > 0) {
            editor.insertText(insertIndex, "\n", "user");
            insertIndex += 1;
          }
          if (line) {
            editor.insertText(insertIndex, line, "user");
            insertIndex += line.length;
          }
        });
        editor.setSelection(insertIndex, 0);
      } catch (err) {
        // fall back to default behavior
      }
    };

    root.addEventListener("copy", onCopyOrCut, true);
    root.addEventListener("cut", onCopyOrCut, true);
    root.addEventListener("paste", onPaste, true);
    return () => {
      root.removeEventListener("copy", onCopyOrCut, true);
      root.removeEventListener("cut", onCopyOrCut, true);
      root.removeEventListener("paste", onPaste, true);
    };
  }, [activeModule, issendQuestionEnabled]);

  return (
    <div className="mt-4">
      <div className="border rounded-3 p-3 bg-light bg-opacity-50">
        <div className="mb-3">
          <label className="form-label fw-bold mb-2">
            Default Module Email Templates
          </label>
          <ul className="nav nav-tabs nav-bordered border-0 d-flex flex-grow-1">
            {TEMPLATE_GROUPS.map((group) => (
              <li key={group.id} className="nav-item me-1 mb-1">
                <button
                  type="button"
                  className={
                    group.modules.some((m) => m.key === activeModule)
                      ? "btn btn-primary active"
                      : "nav-link"
                  }
                  onClick={() => setActiveModule(group.modules[0].key)}
                >
                  {group.label}
                </button>
              </li>
            ))}
            {feedbackModuleConfig && (
              <li key={FEEDBACK_TAB_KEY} className="nav-item me-1 mb-1">
                <button
                  type="button"
                  className={
                    isFeedbackTab ? "btn btn-primary active" : "nav-link"
                  }
                  onClick={() => setActiveModule(FEEDBACK_TAB_KEY)}
                >
                  Get Feedback
                </button>
              </li>
            )}
          </ul>
          {/* Sub-tabs of a group, e.g. Single / Multiple Shipments. */}
          {(() => {
            const group = TEMPLATE_GROUPS.find((g) =>
              g.modules.some((m) => m.key === activeModule),
            );
            if (!group || group.modules.length < 2) return null;
            return (
              <div className="btn-group mt-2" role="group">
                {group.modules.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    className={`btn btn-sm border-primary ${
                      activeModule === m.key
                        ? "btn-primary"
                        : "bg-white text-primary"
                    }`}
                    onClick={() => setActiveModule(m.key)}
                  >
                    {m.subLabel || m.label}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
        {!isFeedbackTab && (
          <>
        <div className="mb-3">
          <label
            htmlFor={`${activeModule}-subject`}
            className="form-label mb-0"
          >
            Subject
          </label>
          <input
            id={`${activeModule}-subject`}
            className="form-control"
            type="text"
            value={emailTemplates[activeModule]?.subject || ""}
            onChange={(e) => updateTemplateField("subject", e.target.value)}
            placeholder={`Enter ${activeModuleConfig?.label} email subject`}
          />
        </div>
        {(showSendPdf ||
          showCcSalesPerson ||
          showSendConfirmation ||
          showsAskQuestion) && (
          <div
            className="d-flex flex-wrap mb-3"
            style={{ columnGap: "2rem", rowGap: "0.75rem" }}
          >
            {showSendPdf && (
              <div>
                <div className="form-check text-nowrap">
                  <input
                    id={`${activeModule}-sendPdf`}
                    className="form-check-input"
                    type="checkbox"
                    checked={emailTemplates[activeModule]?.sendPdf || false}
                    onChange={(e) =>
                      updateTemplateField("sendPdf", e.target.checked)
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`${activeModule}-sendPdf`}
                  >
                    Send attachments
                  </label>
                </div>
              </div>
            )}

            {showCcSalesPerson && (
              <div>
                <div className="form-check text-nowrap">
                  <input
                    id={`${activeModule}-ccSalesPerson`}
                    className="form-check-input"
                    type="checkbox"
                    checked={
                      emailTemplates[activeModule]?.ccSalesPerson || false
                    }
                    onChange={(e) =>
                      updateTemplateField("ccSalesPerson", e.target.checked)
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`${activeModule}-ccSalesPerson`}
                  >
                    Send email to outside sales person
                  </label>
                </div>
              </div>
            )}

            {showSendConfirmation && (
              <div>
                <div className="form-check text-nowrap">
                  <input
                    id={`${activeModule}-sendConfirmation`}
                    className="form-check-input"
                    type="checkbox"
                    checked={
                      emailTemplates[activeModule]?.sendConfirmation || false
                    }
                    onChange={(e) =>
                      updateTemplateField("sendConfirmation", e.target.checked)
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`${activeModule}-sendConfirmation`}
                  >
                    Send Approval Confirmation Email
                  </label>
                </div>
              </div>
            )}

            {showsAskQuestion && (
              <div>
                <div className="form-check text-nowrap">
                  <input
                    id={`${activeModule}-sendQuestion`}
                    className="form-check-input"
                    type="checkbox"
                    checked={
                      emailTemplates[activeModule]?.sendQuestion || false
                    }
                    onChange={(e) =>
                      updateTemplateField("sendQuestion", e.target.checked)
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`${activeModule}-sendQuestion`}
                  >
                    Ask a question
                  </label>
                </div>
              </div>
            )}

          </div>
        )}
        <div
          className="mb-3 d-flex bg-secondary bg-opacity-25 p-1 px-2"
          style={{ borderRadius: 5 }}
        >
          <label className="form-label mb-0 me-3 fw-bolder align-self-center">
            Variables
          </label>
          <div className="d-flex flex-wrap pt-1">
            {Object.keys(activeModuleConfig?.variables || {}).map((key) => (
              <button
                key={key}
                type="button"
                className="bg-white me-1 mb-1 px-2 py-1 btn fw-bold"
                style={{ borderRadius: 10, boxShadow: "1px 1px 3px gray" }}
                onClick={() =>
                  insertVariable(activeModuleConfig.variables[key])
                }
              >
                {key}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white">
          {activeModuleConfig?.lexical ? (
            <EmailBodyEditor
              key={activeModule}
              editorRef={lexicalRef}
              html={emailTemplates[activeModule]?.format || ""}
              loadKey={`${activeModule}|${lexicalLoad}`}
              onChange={(value) => {
                lexicalOwnHtml.current = value;
                updateTemplateField("format", value);
              }}
            />
          ) : (
            <ReactQuill
              key={activeModule}
              ref={quillRef}
              modules={LOCATION_EMAIL_EDITOR_MODULES}
              theme="snow"
              value={emailTemplates[activeModule]?.format || ""}
              onChange={(value) => updateTemplateField("format", value)}
            />
          )}
        </div>
        {/* Confirmation Email Template — shown when sendConfirmation is checked */}
        {showSendConfirmation &&
          emailTemplates[activeModule]?.sendConfirmation &&
          activeModuleConfig?.confirmationFormatKey && (
            <div className="mt-4 border rounded-3 p-3">
              <label className="form-label fw-bold mb-2">
                Approval Confirmation Email Template
              </label>

              <div className="mb-3">
                <label
                  htmlFor={`${activeModule}-confirmationSubject`}
                  className="form-label mb-0"
                >
                  Confirmation Subject{" "}
                  {confirmationErrors.subject && (
                    <span className="text-danger fw-normal ms-1">
                      (required)
                    </span>
                  )}
                </label>
                <input
                  id={`${activeModule}-confirmationSubject`}
                  className={`form-control${
                    confirmationErrors.subject ? " border-danger" : ""
                  }`}
                  type="text"
                  value={
                    emailTemplates[activeModule]?.confirmationSubject || ""
                  }
                  onChange={(e) =>
                    updateTemplateField("confirmationSubject", e.target.value)
                  }
                  placeholder="Enter confirmation email subject"
                />
                {confirmationErrors.subject && (
                  <div className="text-danger small mt-1">
                    Confirmation subject is required when &quot;Send
                    Confirmation&quot; is enabled.
                  </div>
                )}
              </div>

              <div
                className="mb-3 d-flex bg-secondary bg-opacity-25 p-1 px-2"
                style={{ borderRadius: 5 }}
              >
                <label className="form-label mb-0 me-3 fw-bolder align-self-center">
                  Variables
                </label>
                <div className="d-flex flex-wrap pt-1">
                  {Object.keys(activeModuleConfig?.variables || {}).map(
                    (key) => (
                      <button
                        key={key}
                        type="button"
                        className="bg-white me-1 mb-1 px-2 py-1 btn fw-bold"
                        style={{
                          borderRadius: 10,
                          boxShadow: "1px 1px 3px gray",
                        }}
                        onClick={() =>
                          insertVariable(
                            activeModuleConfig.variables[key],
                            confirmationQuillRef,
                          )
                        }
                      >
                        {key}
                      </button>
                    ),
                  )}
                </div>
              </div>

              <div
                className="bg-white"
                style={{
                  height: 240,
                  border: confirmationErrors.format
                    ? "1px solid #dc3545"
                    : undefined,
                  borderRadius: confirmationErrors.format ? 4 : undefined,
                }}
              >
                <ReactQuill
                  key={`${activeModule}-confirmation`}
                  ref={confirmationQuillRef}
                  modules={LOCATION_EMAIL_EDITOR_MODULES}
                  theme="snow"
                  style={{
                    height: 200,
                  }}
                  value={emailTemplates[activeModule]?.confirmationFormat || ""}
                  onChange={(value) =>
                    updateTemplateField("confirmationFormat", value)
                  }
                />
              </div>
              {confirmationErrors.format && (
                <div className="text-danger small mt-1">
                  Confirmation template is required when &quot;Send
                  Confirmation&quot; is enabled.
                </div>
              )}
            </div>
          )}
        {/* Question Email Template — shown when sendQuestion is checked */}
        {showsAskQuestion &&
          emailTemplates[activeModule]?.sendQuestion &&
          activeModuleConfig?.questionFormatKey && (
            <div className="mt-4 border rounded-3 p-3">
              <label className="form-label fw-bold mb-2">
                Question Email Template
              </label>

              <div className="mb-3">
                <label
                  htmlFor={`${activeModule}-questionSubject`}
                  className="form-label mb-0"
                >
                  Question Subject{" "}
                  {questionErrors.subject && (
                    <span className="text-danger fw-normal ms-1">
                      (required)
                    </span>
                  )}
                </label>
                <input
                  id={`${activeModule}-questionSubject`}
                  className={`form-control${
                    questionErrors.subject ? " border-danger" : ""
                  }`}
                  type="text"
                  value={emailTemplates[activeModule]?.questionSubject || ""}
                  onChange={(e) =>
                    updateTemplateField("questionSubject", e.target.value)
                  }
                  placeholder="Enter question email subject"
                />
                {questionErrors.subject && (
                  <div className="text-danger small mt-1">
                    Question subject is required when &quot;Ask a question&quot;
                    is enabled.
                  </div>
                )}
              </div>

              <div
                className="mb-3 d-flex bg-secondary bg-opacity-25 p-1 px-2"
                style={{ borderRadius: 5 }}
              >
                <label className="form-label mb-0 me-3 fw-bolder align-self-center">
                  Variables
                </label>
                <div className="d-flex flex-wrap pt-1">
                  {Object.keys(activeModuleConfig?.variables || {}).map(
                    (key) => (
                      <button
                        key={key}
                        type="button"
                        className="bg-white me-1 mb-1 px-2 py-1 btn fw-bold"
                        style={{
                          borderRadius: 10,
                          boxShadow: "1px 1px 3px gray",
                        }}
                        onClick={() =>
                          insertVariable(
                            activeModuleConfig.variables[key],
                            questionQuillRef,
                          )
                        }
                      >
                        {key}
                      </button>
                    ),
                  )}
                </div>
              </div>

              <div
                className="bg-white"
                style={{
                  height: 240,
                  border: questionErrors.format
                    ? "1px solid #dc3545"
                    : undefined,
                  borderRadius: questionErrors.format ? 4 : undefined,
                }}
              >
                <ReactQuill
                  key={`${activeModule}-question`}
                  ref={questionQuillRef}
                  modules={LOCATION_EMAIL_EDITOR_MODULES}
                  theme="snow"
                  style={{
                    height: 200,
                  }}
                  value={emailTemplates[activeModule]?.questionFormat || ""}
                  onChange={(value) =>
                    updateTemplateField("questionFormat", value)
                  }
                />
              </div>
              {questionErrors.format && (
                <div className="text-danger small mt-1">
                  Question template is required when &quot;Ask a question&quot;
                  is enabled.
                </div>
              )}
            </div>
          )}
          </>
        )}
        {/* Feedback tab — promoted from a "Get Feedback" checkbox on the Order
            Approval tab to its own tab. The data still lives on the approval
            template row (feedbackSubject / feedbackTemplate / sendFeedback).
            Body is raw editable HTML (a plain textarea + live preview), not a
            ReactQuill instance, per explicit request. */}
        {isFeedbackTab && activeModuleConfig?.feedbackFormatKey && (
          <>
            <div
              className="d-flex flex-wrap mb-3"
              style={{ columnGap: "2rem", rowGap: "0.75rem" }}
            >
              <div className="form-check text-nowrap">
                <input
                  id={`${activeStorageKey}-sendFeedback`}
                  className="form-check-input"
                  type="checkbox"
                  checked={activeTemplate?.sendFeedback || false}
                  onChange={(e) =>
                    updateTemplateField("sendFeedback", e.target.checked)
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`${activeStorageKey}-sendFeedback`}
                >
                  Get Feedback (send a feedback request email)
                </label>
              </div>
            </div>
            <div className="mt-4 border rounded-3 p-3">
              <label className="form-label fw-bold mb-2">
                Feedback Email Template
              </label>

              <div className="mb-3">
                <label
                  htmlFor={`${activeModule}-feedbackSubject`}
                  className="form-label mb-0"
                >
                  Feedback Subject{" "}
                  {feedbackErrors.subject && (
                    <span className="text-danger fw-normal ms-1">
                      (required)
                    </span>
                  )}
                </label>
                <input
                  id={`${activeModule}-feedbackSubject`}
                  className={`form-control${
                    feedbackErrors.subject ? " border-danger" : ""
                  }`}
                  type="text"
                  value={activeTemplate?.feedbackSubject || ""}
                  onChange={(e) =>
                    updateTemplateField("feedbackSubject", e.target.value)
                  }
                  placeholder="Enter feedback email subject"
                />
                {feedbackErrors.subject && (
                  <div className="text-danger small mt-1">
                    Feedback subject is required when &quot;Get
                    Feedback&quot; is enabled.
                  </div>
                )}
              </div>

              <div
                className="mb-3 d-flex bg-secondary bg-opacity-25 p-1 px-2"
                style={{ borderRadius: 5 }}
              >
                <label className="form-label mb-0 me-3 fw-bolder align-self-center">
                  Variables
                </label>
                <div className="d-flex flex-wrap pt-1">
                  {Object.keys(activeModuleConfig?.variables || {}).map(
                    (key) => (
                      <button
                        key={key}
                        type="button"
                        className="bg-white me-1 mb-1 px-2 py-1 btn fw-bold"
                        style={{
                          borderRadius: 10,
                          boxShadow: "1px 1px 3px gray",
                        }}
                        onClick={() =>
                          insertVariableIntoFeedbackTextarea(
                            activeModuleConfig.variables[key],
                          )
                        }
                      >
                        {key}
                      </button>
                    ),
                  )}
                </div>
              </div>

              <label
                htmlFor={`${activeModule}-feedbackFormat`}
                className="form-label mb-0"
              >
                HTML Code
              </label>
              <textarea
                id={`${activeModule}-feedbackFormat`}
                ref={feedbackTextareaRef}
                className={`form-control font-monospace${
                  feedbackErrors.format ? " border-danger" : ""
                }`}
                style={{ height: 240, fontSize: 13 }}
                spellCheck={false}
                value={activeTemplate?.feedbackFormat || ""}
                onChange={(e) =>
                  updateTemplateField("feedbackFormat", e.target.value)
                }
                placeholder="<p>Enter the feedback email HTML here...</p>"
              />
              {feedbackErrors.format && (
                <div className="text-danger small mt-1">
                  Feedback template is required when &quot;Get
                  Feedback&quot; is enabled.
                </div>
              )}

              <label className="form-label mb-0 mt-3">Preview</label>
              <div
                className="bg-white border rounded-2 p-3"
                dangerouslySetInnerHTML={{
                  __html:
                    activeTemplate?.feedbackFormat ||
                    "<span class='text-muted'>Nothing to preview yet.</span>",
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LocationEmailTemplateFields;
